import React, { Suspense, useState } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import GlobalHeader from '@nextiva/global-header__simple-header';
import { useFeature } from '@nextiva/feature-toggle';
import cx from 'classnames';
import {
  Layout,
  Main,
  NotFoundPlaceholder,
  ThemeProvider,
} from '@nextiva/next-ui';
import { Alert } from '@material-ui/lab';
import { SideNav } from 'UIElements';
import { GLOBAL_NAVIGATION, MOONSHOT_OUTAGE_FLAG } from 'constant';
import 'styles/base.scss';

import ROUTES from './App.routes';
import './App.scss';

import { usePendo } from '../hooks/usePendo';
import { useGetUserLicensesWithAttributes } from '../hooks/useGetUserLicensesWithAttributes.ts';
import { getNextivaCXFreeTrialAttributes } from '../utils/getNextivaCXFreeTrialAttributes.ts';
import { getLicenseFeatures } from 'utils/licenseFeaturesHelpers';

const blockName = 'billing';

const widgets = {
  calendar: null,
};

const App = ({ globalStoreData }) => {
  const {
    data: userLicensesWithAttributes,
    isLoading: isUserLicensesWithAttributesLoading,
  } = useGetUserLicensesWithAttributes();
  const nextivaCXFreeTrialAttributes = getNextivaCXFreeTrialAttributes(
    userLicensesWithAttributes?.data
  );

  usePendo(
    isUserLicensesWithAttributesLoading,
    nextivaCXFreeTrialAttributes,
    getLicenseFeatures(globalStoreData?.licenses)
  );

  const [showAlert, setShowAlert] = useState(true);
  const { isReady, isEnabled } = useFeature({ name: GLOBAL_NAVIGATION });

  const isNewLayout = isReady && isEnabled;

  const { isEnabled: moonshotOutage } = useFeature({
    name: MOONSHOT_OUTAGE_FLAG,
  });
  const closeAlert = () => {
    setShowAlert(false);
  };

  return (
    <ThemeProvider theme="modern">
      <Layout className={cx(isNewLayout && 'new-layout')}>
        <aside className="nx-aside">
          <SideNav {...globalStoreData} />
        </aside>
        <div className="nx-header">
          <GlobalHeader
            appName={isEnabled ? 'Administration' : 'Administration Tools'}
            firstName={globalStoreData.user.firstName || ''}
            lastName={globalStoreData.user.lastName || ''}
            isLoading={false}
            isHeaderLogoVisible={false}
            widgets={widgets}
            globalStoreData={globalStoreData}
          />
        </div>
        <Main>
          {moonshotOutage && showAlert && (
            <Alert
              severity="error"
              className="moonshot-alert"
              onClose={closeAlert}
            >
              Payment is currently disabled due to maintenance. Please try back
              later.
            </Alert>
          )}
          <div className={blockName}>
            <Suspense
              fallback={<div className="loading-fallback">Loading...</div>}
            >
              <Switch>
                <Route
                  path={ROUTES.LANDING.path}
                  component={ROUTES.LANDING.getComponent}
                />
                <Route
                  path={ROUTES.FORBIDDEN.path}
                  component={ROUTES.FORBIDDEN.getComponent}
                />
                <Redirect exact from="/" to="landing/summary" />
                <Route path="/404">
                  <NotFoundPlaceholder
                    className={`${blockName}__404`}
                    title="Error 404"
                    text="Invalid page"
                  />
                </Route>
                <Route path="*">
                  <Redirect to="/404" />
                </Route>
              </Switch>
            </Suspense>
          </div>
        </Main>
      </Layout>
    </ThemeProvider>
  );
};

App.propTypes = {
  globalStoreData: PropTypes.object.isRequired,
};

App.defaultProps = {
  globalStoreData: {},
};

export default App;
